/**
 * `runSteps` (1.Y deliverable 1): runs `planSince`'s steps one at a time, in order. When a step
 * fails, the earlier steps' compensations run, newest first (an ended state or link gets its old
 * `to` or `validTo` back), and then the failure is reported, so a refused change leaves the field
 * as it was. A create is always a plan's last step and needs no compensation.
 */

import type { TemporalApi } from './api.js';
import type { DatedStep } from './dated-model.js';

export class StepsFailedError extends Error {
  override readonly name = 'StepsFailedError';
  constructor(
    /** The route's own error, for the refusal copy. */
    override readonly cause: unknown,
    /** Whether every compensation ran; a failed one leaves its row ended. */
    readonly compensated: boolean,
  ) {
    super('a step of the dated change failed');
  }
}

type Undo = () => Promise<unknown>;

/** One step; answers what undoes it, or null. */
async function runOne(step: DatedStep, client: TemporalApi): Promise<Undo | null> {
  switch (step.op) {
    case 'states.update': {
      const row = await client.updateState(step.docId, step.stateId, step.body);
      return () =>
        client.updateState(step.docId, step.stateId, { baseRv: row.rv, ...step.restore });
    }
    case 'states.create':
      await client.createState(step.docId, step.body);
      return null;
    case 'links.update': {
      const row = await client.updateLink(step.sourceId, step.linkId, step.body);
      return () =>
        client.updateLink(step.sourceId, step.linkId, { baseRv: row.rv, ...step.restore });
    }
    case 'links.create':
      await client.createLink(step.sourceId, step.body);
      return null;
  }
}

export async function runSteps(steps: readonly DatedStep[], client: TemporalApi): Promise<void> {
  const undo: Undo[] = [];
  for (const step of steps) {
    try {
      const back = await runOne(step, client);
      if (back !== null) undo.push(back);
    } catch (error) {
      let compensated = true;
      for (const back of [...undo].reverse()) {
        try {
          await back();
        } catch {
          compensated = false;
        }
      }
      throw new StepsFailedError(error, compensated);
    }
  }
}
